import { forwardRef, useEffect, useRef, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import useImageLoader, { normalizeFileId } from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import useVideoReady from '@/common/hooks/useVideoReady';
import { playVideoSafely } from '@/common/functions/videoElement';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { formatTranslation } from '@/common/utils/formatTranslation';
import { getProfileDirectSrc } from '../../utils/profileMedia';
import {
	DiscoverBio,
	DiscoverCardRoot,
	DiscoverChatButton,
	DiscoverImage,
	DiscoverInfo,
	DiscoverMediaSkeleton,
	DiscoverShade,
	DiscoverTitle,
	DiscoverVideo,
} from './discoverCard.styles';

const DiscoverCard = forwardRef(({ profile, isActive, onClick }, ref) => {
	const t = useLanguageData('profileCard');
	const profileModal = useLanguageData('profileModal');
	const profileMediaId = normalizeFileId(
		profile?.profile_image?._id ?? profile?.profile_image,
	);
	const directImageSrc = getProfileDirectSrc(profile);
	const { imageSrc, isLoading: isImageFileLoading } = useImageLoader(profileMediaId);
	const resolvedImageSrc = imageSrc || directImageSrc;
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [hasImageError, setHasImageError] = useState(false);
	const imageRef = useRef(null);

	const videoMediaId = normalizeFileId(
		profile?.profile_video?._id ?? profile?.profile_video,
	);
	const { videoSrc } = useVideoLoader(videoMediaId);
	const videoRef = useRef(null);
	const videoRemountKey = `${videoMediaId ?? ''}-${videoSrc ?? ''}`;
	const isVideoReady = useVideoReady(videoRef, videoSrc, videoRemountKey);

	const isImageFileReady = !profileMediaId || !isImageFileLoading;
	const showImageSkeleton =
		!isImageFileReady
		|| !resolvedImageSrc
		|| hasImageError
		|| !isImageLoaded;

	const hasVideo = Boolean(videoSrc);
	const showVideo = hasVideo && isActive && isVideoReady;

	useEffect(() => {
		setIsImageLoaded(false);
		setHasImageError(false);
	}, [resolvedImageSrc]);

	useEffect(() => {
		const element = imageRef.current;

		if (!element || !resolvedImageSrc || hasImageError) {
			return;
		}

		if (element.complete && element.naturalWidth > 0) {
			setIsImageLoaded(true);
		}
	}, [resolvedImageSrc, hasImageError]);

	useEffect(() => {
		const video = videoRef.current;

		if (!video || !videoSrc || !isActive || !isVideoReady) {
			if (video) {
				video.pause();
				video.currentTime = 0;
			}
			return;
		}

		playVideoSafely(video);
	}, [isActive, isVideoReady, videoSrc]);

	const handleImageLoad = () => {
		setIsImageLoaded(true);
		setHasImageError(false);
	};

	const handleImageError = () => {
		setIsImageLoaded(false);
		setHasImageError(true);
	};

	const description = profile.description || profile.person_config?.description;

	return (
		<DiscoverCardRoot
			ref={ref}
			onClick={() => onClick(profile)}
			onKeyDown={(event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					onClick(profile);
				}
			}}
			role="button"
			tabIndex={0}
		>
			{showImageSkeleton && (
				<DiscoverMediaSkeleton>
					<Skeleton
						width="100%"
						height="100%"
						radius="0"
						minWidth="100%"
						minHeight="100%"
					/>
				</DiscoverMediaSkeleton>
			)}
			{resolvedImageSrc && !hasImageError && (
				<DiscoverImage
					ref={imageRef}
					src={resolvedImageSrc}
					alt={formatTranslation(t.portraitAlt, { name: profile.name })}
					$visible={!showVideo}
					onLoad={handleImageLoad}
					onError={handleImageError}
				/>
			)}
			{hasVideo && (
				<DiscoverVideo
					ref={videoRef}
					key={videoRemountKey}
					src={videoSrc}
					muted
					loop
					playsInline
					preload="auto"
					draggable={false}
					poster={isImageLoaded ? resolvedImageSrc : undefined}
					$visible={showVideo}
				/>
			)}
			<DiscoverShade />
			<DiscoverInfo>
				<DiscoverTitle>
					<h2>
						{profile.name}
						{profile.age ? (
							<span>
								{' '}
								{profile.age}
								{' '}
								{t.ageShort}
							</span>
						) : null}
					</h2>
				</DiscoverTitle>
				{description && <DiscoverBio>{description}</DiscoverBio>}
				<DiscoverChatButton
					type="button"
					onClick={(event) => {
						event.stopPropagation();
						onClick(profile);
					}}
				>
					{profileModal.startChatting}
				</DiscoverChatButton>
			</DiscoverInfo>
		</DiscoverCardRoot>
	);
});

DiscoverCard.displayName = 'DiscoverCard';

export default DiscoverCard;
